import React, { useCallback, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import cx from 'classnames';
import { ArrowLeft, MessageSquarePlus } from 'lucide-react';
import { useCreateTemplateMessageMutation } from '@@/services/templateMessage';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import TemplateMessageForm, {
  schema,
} from '@@/components/Forms/TemplateMessageForm';

type FormType = {
  title: string;
  body: string;
};

const NewTemplateMessagePage: React.FC = () => {
  const navigate = useNavigate();
  const [createTemplateMessage, { isSuccess }] =
    useCreateTemplateMessageMutation();

  const form = useForm<FormType>({
    resolver: yupResolver(schema),
    mode: 'onBlur',
  });

  const onSubmit = useCallback(
    async (data: FormType) => {
      createTemplateMessage(data);
    },
    [createTemplateMessage],
  );

  useEffect(() => {
    if (isSuccess) {
      toast.success(`Votre message a bien été programmé`, {
        position: 'top-right',
      });

      navigate('/template-messages');
    }
  }, [isSuccess]);

  return (
    <div className="px-page-sm py-6 lg:px-page lg:py-8 w-full">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
          <div className="flex items-center">
            <MessageSquarePlus size={28} className="text-neutral-900" />
            <h1 className="text-heading-lg text-neutral-900 ml-3">Nouveau message type</h1>
          </div>

          <Link
            to="/template-messages"
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-white hover:bg-neutral-50 text-neutral-700 font-medium rounded-button border border-neutral-200 transition-colors duration-200 shadow-sm"
          >
            <ArrowLeft size={18} />
            <span className="text-body-sm">Retour</span>
          </Link>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-card shadow-card border border-neutral-200 p-6">
          <TemplateMessageForm form={form} />

          <div className="mt-6 pt-6 border-t border-neutral-200 flex justify-end">
            <button
              type="button"
              onClick={form.handleSubmit(onSubmit)}
              disabled={form.formState.isSubmitting}
              className={cx(
                'inline-flex items-center px-6 py-3 text-sm font-medium text-white rounded-button transition-colors duration-200 shadow-button',
                form.formState.isSubmitting
                  ? 'bg-neutral-400 cursor-not-allowed'
                  : 'bg-primary-600 hover:bg-primary-700',
              )}
            >
              {form.formState.isSubmitting ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-3 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Création...
                </>
              ) : (
                'Créer le message type'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewTemplateMessagePage;
